'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type React from 'react';
import Like from '@/features/Like/Like';
import { useApp } from '@/forApp/states/appState/appState';
import type { BaseEntityT } from '@/forApp/types/Entity';
import Button from '@/shared/Buttons/Button';
import IconButton from '@/shared/Buttons/IconButton/IconButton';
import { Icons } from '@/shared/Icons/Icons';
import ImageWithLoader from '@/shared/Image/ImageWithLoader';
import Prices from '@/shared/Prices';
import { getRouteHref, ROUTES } from '@/forApp/utils/routes';

import styles from './styles.module.css';

type ProfileObjectCardProps = {
  card: BaseEntityT;
  type: 'promo' | 'favorite';
  mutate?: () => void;
  profile?: {
    role: string;
  };
  typeObj: string;
};

const ProfileObjectMobileCard: React.FC<ProfileObjectCardProps> = ({
  card,
  type,
  mutate,
  profile = { role: 'user' },
  typeObj,
}) => {
  const router = useRouter();
  const { addModal } = useApp();
  const goToEdit = (slug: string, type: string) => {
    router.push(`/profile/edit/${type}/${slug}`);
  };

  const onArchive = () => {
    addModal({
      name: 'ArchiveObjectModal',
      object: card,
      mutate,
    });
  };

  const onButtonClick = () => {
    addModal({ name: 'ConsultationModal', title: 'Заказать звонок', from: '' });
  };

  const onDelete = () => {
    addModal({
      name: 'DeleteObjectModal',
      object: card,
      mutate,
    });
  };

  const cardThumbnail = card?.images?.length
    ? card.images[0]?.thumbnail_path || '/imgs/no-photo.png'
    : '/imgs/no-photo.png';

  return (
    <Link
      href={getRouteHref(ROUTES.ENTITY, { type: typeObj, slug: card?.slug })}
      className={styles.profileObjectItem}
      style={{ minHeight: card.images.length > 1 ? '359px' : '278px' }}
      data-name="ProfileObjectMobileCard"
    >
      <div
        className={
          type === 'promo'
            ? styles.profileObjectItemImage
            : styles.profileObjectItemImage
        }
      >
        {type === 'promo' && card.status === 'in_moderation' && (
          <div className={styles.badge}>На модерации</div>
        )}
        {type === 'promo' && card.status === 'archive' && (
          <div className={styles.badge}>В архиве</div>
        )}
        <ImageWithLoader
          src={cardThumbnail ? cardThumbnail : '/imgs/no-photo.png'}
          alt="Фото объекта"
          width={window.innerWidth - 32}
          height={263}
          objectFit={'cover'}
        />
        {card.images.length > 1 ? (
          <div className={styles.profileObjectItemCounter}>
            {card.images.slice(1, 4).map((image, index) => {
              const isOverlayForManyImages =
                card.images.length > 4 && index === 2;

              const isExactlyFourImages =
                card.images.length === 4 && index === 2;
              const isThreeImages = card.images.length === 3 && index === 1;

              const isTwoImages = card.images.length === 2 && index === 0;

              const isLastThumbnail =
                isOverlayForManyImages ||
                isExactlyFourImages ||
                isThreeImages ||
                isTwoImages;
              return (
                <div key={index} className={styles.thumbnailWrapper}>
                  <ImageWithLoader
                    src={
                      image.thumbnail_path
                        ? image.thumbnail_path
                        : '/imgs/no-photo.png'
                    }
                    alt="Фото объекта"
                    width={118}
                    height={88}
                    objectFit={'cover'}
                  />
                  {isLastThumbnail && (
                    <div className={styles.morePhotosOverlay}>
                      <span>Ещё фото</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      <div className={styles.profileObjectItemContent}>
        <div className={styles.blockHead}>
          <h4 className={styles.title}>{card?.name}</h4>
          <div className={styles.profileObjectItemAction}>
            {type === 'promo' ? (
              profile.role !== 'admin_agency' && (
                <div className={styles.iconButtonsContainer}>
                  <IconButton
                    icon={<Icons.Pencil />}
                    onClick={() => {
                      goToEdit(card.slug, card.entity_type.slug);
                    }}
                    className={styles.editButton}
                    ariaLabel="Edit"
                    tooltip={'Редактировать'}
                    tooltipPosition="top"
                  />
                  {card.status !== 'archive' && (
                    <IconButton
                      icon={<Icons.ArrowArchive />}
                      onClick={onArchive}
                      className={styles.archiveButton}
                      ariaLabel="Archive"
                      tooltip={'Добавить в архив'}
                      tooltipPosition="top"
                    />
                  )}
                  <IconButton
                    icon={<Icons.Close />}
                    onClick={onDelete}
                    className={styles.deleteButton}
                    ariaLabel="Delete"
                    tooltip={'Удалить'}
                    tooltipPosition="top"
                  />
                </div>
              )
            ) : (
              <Like entity_id={card?.id} />
            )}
          </div>
        </div>
        <span className={styles.address}>{card?.full_address}</span>
        <div className={styles.blockHead}>
          <Prices
            price={card?.price}
            priceMeter={card?.price_meter}
            totalArea={card?.total_area}
          />
          <Button
            onClick={onButtonClick}
            aria-describedby="consultation-title"
            text={'Заказать звонок'}
            color={'white'}
            className={styles.btnMain}
          />
        </div>
        {/*   <p className={styles.description}>{ card?.additional_info || '' }</p>
         */}{' '}
      </div>
    </Link>
  );
};

export default ProfileObjectMobileCard;
